import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  Users,
  BarChart3,
  PieChart,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Package,
  Sparkles,
} from "lucide-react";
import { getPricingRuleAnalyticsThunk } from "../../../store/thunks/pricingThunks";
import {
  selectRuleAnalytics,
  selectAnalyticsLoading,
  selectAnalyticsError,
} from "../../../store/slices/pricingSlice";

// Skeleton Loader
const AnalyticsSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    <div className="flex justify-between items-center">
      <div className="h-8 w-64 bg-neutral-light/50 rounded" />
      <div className="h-10 w-40 bg-neutral-light/50 rounded-xl" />
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-32 bg-neutral-light/30 rounded-2xl" />
      ))}
    </div>
    <div className="h-80 bg-neutral-light/30 rounded-2xl" />
    <div className="grid grid-cols-2 gap-6">
      <div className="h-64 bg-neutral-light/30 rounded-2xl" />
      <div className="h-64 bg-neutral-light/30 rounded-2xl" />
    </div>
  </div>
);

// Stat Card Component
const StatCard = ({
  icon: Icon,
  label,
  value,
  trend,
  trendLabel,
  color,
  gradient,
}) => (
  <div className={`relative overflow-hidden rounded-2xl p-5 ${gradient}`}>
    {/* Background Pattern */}
    <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
      <Icon className="w-full h-full" />
    </div>

    <div className="relative">
      <div className="flex items-center justify-between mb-3">
        <div
          className={`w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center`}
        >
          <Icon className="w-6 h-6 text-white" />
        </div>
        {trend !== undefined && (
          <div
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
              trend >= 0 ? "bg-white/20 text-white" : "bg-white/20 text-white"
            }`}
          >
            {trend >= 0 ? (
              <ArrowUpRight className="w-3 h-3" />
            ) : (
              <ArrowDownRight className="w-3 h-3" />
            )}
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <p className="text-3xl font-bold text-white mb-1">{value}</p>
      <p className="text-white/80 text-sm">{label}</p>
      {trendLabel && <p className="text-white/60 text-xs mt-1">{trendLabel}</p>}
    </div>
  </div>
);

// Bar Chart Component
const BarChart = ({ data, maxValue }) => (
  <div className="space-y-3">
    {data.map((item, index) => {
      const percentage = maxValue > 0 ? (item.count / maxValue) * 100 : 0;
      return (
        <div key={index} className="group">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-medium text-neutral-dark">
              {item.day}
            </span>
            <span className="text-sm font-semibold text-primary">
              {item.count}
            </span>
          </div>
          <div className="h-8 bg-neutral-lightest rounded-lg overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-primary-dark rounded-lg transition-all duration-500 ease-out group-hover:from-primary-dark group-hover:to-primary flex items-center justify-end pr-3"
              style={{ width: `${Math.max(percentage, 2)}%` }}
            >
              {percentage > 15 && (
                <span className="text-xs font-medium text-white">
                  {item.count}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

// Progress Bar Component
const ProgressBar = ({ label, count, percentage, color, index }) => {
  const colors = [
    "from-primary to-primary-dark",
    "from-success to-success-dark",
    "from-warning to-warning-dark",
    "from-secondary to-secondary-dark",
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-neutral-dark">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral">{count}</span>
          <span className="text-sm font-semibold text-neutral-dark">
            {percentage}%
          </span>
        </div>
      </div>
      <div className="h-3 bg-neutral-lightest rounded-full overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${
            colors[index % colors.length]
          } rounded-full transition-all duration-700 ease-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Product Card Component
const ProductCard = ({ product, index }) => (
  <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-neutral-lightest/50 to-white rounded-xl border border-neutral-light/30 hover:shadow-md transition-all">
    <div
      className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white ${
        index === 0
          ? "bg-gradient-to-br from-yellow-400 to-yellow-600"
          : index === 1
          ? "bg-gradient-to-br from-gray-300 to-gray-500"
          : index === 2
          ? "bg-gradient-to-br from-amber-600 to-amber-800"
          : "bg-gradient-to-br from-primary to-primary-dark"
      }`}
    >
      {index + 1}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-neutral-dark truncate">{product.name}</p>
      <p className="text-sm text-neutral">{product.count} applications</p>
    </div>
    <div className="text-right">
      <p className="font-bold text-success-dark">
        ₹{(product.revenue / 1000).toFixed(1)}K
      </p>
      <p className="text-xs text-neutral">Revenue</p>
    </div>
  </div>
);

// Metric Card Component
const MetricCard = ({ value, label, icon: Icon, color }) => (
  <div className="bg-gradient-to-br from-neutral-lightest/50 to-white rounded-xl p-5 border border-neutral-light/30 text-center hover:shadow-md transition-all">
    <div
      className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-${color}/10 flex items-center justify-center`}
    >
      <Icon className={`w-6 h-6 text-${color}`} />
    </div>
    <p className={`text-2xl font-bold text-${color} mb-1`}>{value}</p>
    <p className="text-sm text-neutral">{label}</p>
  </div>
);

const RuleAnalytics = ({ ruleId, ruleName }) => {
  const dispatch = useDispatch();
  const analyticsData = useSelector(selectRuleAnalytics);
  const loading = useSelector(selectAnalyticsLoading);
  const error = useSelector(selectAnalyticsError);

  const [dateRange, setDateRange] = useState("30d");

  useEffect(() => {
    dispatch(getPricingRuleAnalyticsThunk({ ruleId, dateRange }));
  }, [dispatch, ruleId, dateRange]);

  if (loading) {
    return <AnalyticsSkeleton />;
  }

  if (error) {
    return (
      <div className="bg-danger/10 border border-danger/20 rounded-2xl p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-danger/20 rounded-full flex items-center justify-center">
          <BarChart3 className="w-8 h-8 text-danger" />
        </div>
        <p className="text-lg font-semibold text-danger mb-2">
          Failed to Load Analytics
        </p>
        <p className="text-neutral-dark">{error}</p>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="bg-neutral-lightest/50 rounded-2xl p-12 text-center border border-dashed border-neutral-light">
        <div className="w-16 h-16 mx-auto mb-4 bg-neutral-light/50 rounded-full flex items-center justify-center">
          <BarChart3 className="w-8 h-8 text-neutral" />
        </div>
        <p className="text-lg font-semibold text-neutral-dark mb-2">
          No Analytics Available
        </p>
        <p className="text-neutral">
          Analytics data will appear here once the rule is used
        </p>
      </div>
    );
  }

  const maxUsageCount = Math.max(
    ...(analyticsData.usageByDay?.map((d) => d.count) || [0])
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl lg:text-2xl font-bold text-neutral-dark flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            Analytics Overview
          </h2>
          <p className="text-neutral mt-1">
            Performance metrics for {ruleName}
          </p>
        </div>

        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral" />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="pl-10 pr-4 py-2.5 bg-white border border-neutral-light rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all appearance-none cursor-pointer"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={ShoppingCart}
          label="Total Applications"
          value={analyticsData.totalUsage?.toLocaleString() || "0"}
          trend={parseFloat(analyticsData.trend) || 0}
          trendLabel="vs previous period"
          gradient="bg-gradient-to-br from-primary to-primary-dark"
        />
        <StatCard
          icon={DollarSign}
          label="Revenue Impact"
          value={`₹${((analyticsData.totalRevenue || 0) / 100000).toFixed(1)}L`}
          gradient="bg-gradient-to-br from-success to-success-dark"
        />
        <StatCard
          icon={Users}
          label="Unique Customers"
          value={analyticsData.uniqueCustomers?.toLocaleString() || "0"}
          gradient="bg-gradient-to-br from-warning to-warning-dark"
        />
        <StatCard
          icon={TrendingUp}
          label="Conversion Rate"
          value={`${analyticsData.conversionRate || 0}%`}
          gradient="bg-gradient-to-br from-secondary to-secondary-dark"
        />
      </div>

      {/* Usage Trend Chart */}
      <div className="bg-white rounded-2xl p-6 border border-neutral-light/50">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-neutral-dark flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Usage Trend
          </h3>
          <span className="text-sm text-neutral">Daily applications</span>
        </div>

        {analyticsData.usageByDay?.length > 0 ? (
          <BarChart data={analyticsData.usageByDay} maxValue={maxUsageCount} />
        ) : (
          <div className="h-48 flex items-center justify-center text-neutral">
            No usage data available
          </div>
        )}
      </div>

      {/* Two Column Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <div className="bg-white rounded-2xl p-6 border border-neutral-light/50">
          <h3 className="text-lg font-semibold text-neutral-dark mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            Top Products
          </h3>

          {analyticsData.topProducts?.length > 0 ? (
            <div className="space-y-3">
              {analyticsData.topProducts.map((product, index) => (
                <ProductCard key={index} product={product} index={index} />
              ))}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-neutral">
              No product data available
            </div>
          )}
        </div>

        {/* Customer Segments */}
        <div className="bg-white rounded-2xl p-6 border border-neutral-light/50">
          <h3 className="text-lg font-semibold text-neutral-dark mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-primary" />
            Customer Segments
          </h3>

          {analyticsData.customerSegments?.length > 0 ? (
            <div className="space-y-4">
              {analyticsData.customerSegments.map((segment, index) => (
                <ProgressBar
                  key={index}
                  label={segment.type}
                  count={segment.count}
                  percentage={segment.percentage}
                  index={index}
                />
              ))}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-neutral">
              No segment data available
            </div>
          )}
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="bg-white rounded-2xl p-6 border border-neutral-light/50">
        <h3 className="text-lg font-semibold text-neutral-dark mb-6">
          Additional Metrics
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            value={`₹${(analyticsData.averageDiscount || 0).toLocaleString()}`}
            label="Avg. Discount per Order"
            icon={DollarSign}
            color="primary"
          />
          <MetricCard
            value={
              analyticsData.uniqueCustomers > 0
                ? (
                    analyticsData.totalUsage / analyticsData.uniqueCustomers
                  ).toFixed(1)
                : "0"
            }
            label="Avg. Uses per Customer"
            icon={Users}
            color="success"
          />
          <MetricCard
            value={`₹${
              analyticsData.totalUsage > 0
                ? (
                    analyticsData.totalRevenue / analyticsData.totalUsage
                  ).toLocaleString()
                : "0"
            }`}
            label="Avg. Order Value"
            icon={ShoppingCart}
            color="warning"
          />
        </div>
      </div>
    </div>
  );
};

export default RuleAnalytics;
